import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { GridToolbar } from "@mui/x-data-grid";

import { DataGrid } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { GlobalState } from "context/GlobalContext";

import axios from "axios";

import UploadFileIcon from "@mui/icons-material/UploadFile";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import EditModal from "./EditModal/EditModal";
import { inputTypes } from "data/pimData";
import FetchNewStoresDialog from "./FetchNewStoresDialog";
import StoreMasterUploadDialog from "./StoreMasterUploadDialog";

const minimalGridSx = {
	height: "100%",
	width: "100%",
	display: "flex",
	flexDirection: "column",
	overflow: "hidden",
	border: "1px solid",
	borderColor: "divider",
	borderRadius: 2,
	bgcolor: "background.paper",
	"& .MuiDataGrid-main": {
		borderRadius: 2,
		flex: 1,
		minHeight: 0,
		overflow: "hidden",
	},
	"& .MuiDataGrid-virtualScroller": {
		overflow: "auto",
	},
	"& .MuiDataGrid-toolbarContainer": {
		padding: "10px 12px 6px",
		borderBottom: "1px solid",
		borderColor: "divider",
		gap: 1,
	},
	"& .MuiDataGrid-toolbarContainer .MuiButton-root": {
		minWidth: "auto",
		padding: "4px 8px",
		borderRadius: 1.5,
		color: "text.secondary",
		fontSize: "0.75rem",
		fontWeight: 500,
	},
	"& .MuiDataGrid-toolbarContainer .MuiInputBase-root": {
		borderRadius: 1.5,
		backgroundColor: "grey.50",
	},
	"& .MuiDataGrid-columnHeaders": {
		backgroundColor: "grey.50",
		borderBottom: "1px solid",
		borderColor: "divider",
	},
	"& .MuiDataGrid-columnHeader": {
		paddingX: 1.5,
	},
	"& .MuiDataGrid-columnHeaderTitle": {
		fontWeight: 500,
		color: "text.secondary",
		textTransform: "uppercase",
		fontSize: "0.7rem",
		letterSpacing: "0.4px",
	},
	"& .MuiDataGrid-columnHeader:focus": { outline: "none" },
	"& .MuiDataGrid-cell": {
		borderBottom: "1px solid",
		borderColor: "divider",
		padding: "4px 12px",
		fontSize: "0.82rem",
		color: "text.primary",
	},
	"& .MuiDataGrid-cell:focus": { outline: "none" },
	"& .MuiDataGrid-row": {
		cursor: "pointer",
		transition: "background-color 120ms ease",
		"&:hover": {
			bgcolor: "grey.50",
		},
	},
	"& .MuiDataGrid-columnSeparator": {
		display: "none",
	},
	"& .MuiDataGrid-footerContainer": {
		borderTop: "1px solid",
		borderColor: "divider",
		minHeight: 44,
		flexShrink: 0,
		bgcolor: "background.paper",
	},
	"& .MuiDataGrid-withBorderColor": {
		borderColor: "divider",
	},
};

const inputColumns = {
	ginesys_store_name: {
		name: "Ginesys Store Name",
		key: "full_name",
		group: "EBO_STORE_POS",
		type: { notNull: true, datatype: "string", unique: true },
		input_type: inputTypes.autoGenerate,
		display: true,
	},
	short_name: {
		name: "Store Short Name",
		key: "short_name",
		group: "EBO_STORE_POS",
		type: { notNull: false, datatype: "string", unique: false },
		input_type: inputTypes.autoGenerate,
		display: true,
	},
	store_code: {
		name: "Store Code",
		key: "site_code",
		group: "EBO_STORE_POS",
		type: { notNull: false, datatype: "string", unique: false },
		input_type: inputTypes.autoGenerate,
		display: true,
	},
	fofo_code: {
		name: "Fofo Code",
		key: "fofo_code",
		group: "EBO_STORE_POS",
		type: { notNull: false, datatype: "string", unique: false },
		input_type: inputTypes.autoGenerate,
		display: true,
	},
	upi_id_1: {
		name: "UPI Id 1",
		key: "upi_id_1",
		group: "EBO_STORE_POS",
		type: { notNull: false, datatype: "string", unique: false },
		input_type: inputTypes.enter,
		display: true,
	},
	upi_id_2: {
		name: "UPI Id 2",
		key: "upi_id_2",
		group: "EBO_STORE_POS",
		type: { notNull: false, datatype: "string", unique: false },
		input_type: inputTypes.enter,
		display: true,
	},
	hdfc_card_tid_1: {
		name: "HDFC Card TID 1",
		key: "card_tid_1",
		group: "EBO_STORE_POS",
		type: { notNull: false, datatype: "string", unique: false },
		input_type: inputTypes.enter,
		display: true,
	},
	hdfc_card_tid_2: {
		name: "HDFC Card TID 2",
		key: "card_tid_2",
		group: "EBO_STORE_POS",
		type: { notNull: false, datatype: "string", unique: false },
		input_type: inputTypes.enter,
		display: true,
	},
	amex_id: {
		name: "Amex Id",
		key: "amex_id",
		group: "EBO_STORE_POS",
		type: { notNull: false, datatype: "string", unique: false },
		input_type: inputTypes.enter,
		display: true,
	},
	writer_cash_pickup: {
		name: "Writer Cash Pickup",
		key: "writer_cash_pickup",
		group: "EBO_STORE_POS",
		type: { notNull: false, datatype: "string", unique: false },
		input_type: inputTypes.enter,
		display: true,
	},
	phonepe_id: {
		name: "Phonepe Id",
		key: "phonepe_id",
		group: "EBO_STORE_POS",
		type: { notNull: false, datatype: "string", unique: false },
		input_type: inputTypes.enter,
		display: true,
	},
	pay_by_link: {
		name: "Pay By Link",
		key: "pay_by_link",
		group: "EBO_STORE_POS",
		type: { notNull: false, datatype: "string", unique: false },
		input_type: inputTypes.enter,
		display: true,
	},
	magicpin_id: {
		name: "Magicpin ID",
		key: "magicpin_id",
		group: "EBO_STORE_POS",
		type: { notNull: false, datatype: "string", unique: false },
		input_type: inputTypes.enter,
		display: true,
	},
	zomato_id: {
		name: "Zomato ID",
		key: "zomato_id",
		group: "EBO_STORE_POS",
		type: { notNull: false, datatype: "string", unique: false },
		input_type: inputTypes.enter,
		display: true,
	},
	status: {
		name: "Status",
		key: "Status",
		group: "EBO_STORE_POS",
		type: { notNull: false, datatype: "string", unique: false },
		input_type: inputTypes.enter,
		display: true,
	},
};

const tableColumns = [
	...Object.keys(inputColumns).map((col) => {
		return {
			field: inputColumns[col].key,
			headerName: inputColumns[col].name,
			flex: 1,
			minWidth: 150,
			headerAlign: "center",
			align: "center",
			sortable: true,
		};
	}),
];

const StoreTidMaster = () => {
	const [allStores, setAllStores] = useState([]);
	const [loading, setLoading] = useState(false);
	const [selectedStore, setSelectedStore] = useState();

	// def value is true becoz for textfield disabled =true;
	const [modalOpen, setModalOpen] = useState(false);
	const [fetchDialogOpen, setFetchDialogOpen] = useState(false);
	const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
	const { setSnackBar, setSnackBarOpen } = GlobalState();

	// console.log(selectedStore, allStores);
	const submitFunction = () => {};
	// fetch costings on load
	const fetchStores = async () => {
		try {
			setLoading(true);
			const { data } = await axios.get("/api/pos_reco/get_store_master_bq");
			const stores = Array.isArray(data) ? data : data?.result || [];
			setAllStores(
				stores.map((store, index) => ({
					...store,
					id: store?.id ?? store?.site_code ?? store?.short_name ?? `store-${index}`,
				})),
			);
			setLoading(false);
		} catch (error) {
			setLoading(false);
			setSnackBarOpen(true);
			setSnackBar({
				message: "Network Error",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		}
	};

	useEffect(() => {
		fetchStores();
	}, []);

	const handleGridRowClick = (data) => {
		setModalOpen(true);
		setSelectedStore(data);
	};
	return (
		<Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
			<Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, pr: 1 }}>
				<Box display="flex" gap={1}>
					<Button
						size="small"
						variant="contained"
						startIcon={<UploadFileIcon />}
						onClick={() => setUploadDialogOpen(true)}>
						Upload
					</Button>
					<Button
						size="small"
						variant="outlined"
						startIcon={<SyncRoundedIcon />}
						onClick={() => setFetchDialogOpen(true)}>
						Fetch New Stores
					</Button>
				</Box>
			</Box>
			<Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
				<DataGrid
					rows={allStores}
					columns={tableColumns}
					sx={minimalGridSx}
					onRowClick={(params) => {
						handleGridRowClick(params.row);
					}}
					initialState={{
						pagination: {
							paginationModel: { pageSize: 25 },
						},
					}}
					loading={loading}
					slots={{
						toolbar: GridToolbar,
						noRowsOverlay: () => (
							<Typography variant="body2" sx={{ mt: 2 }}>
								No stores found
							</Typography>
						),
					}}
					slotProps={{
						toolbar: {
							showQuickFilter: true,
							csvOptions: { fileName: "pos_store_master" },
						},
					}}
					density="compact"
					disableRowSelectionOnClick
					pageSizeOptions={[10, 25, 50]}
				/>
			</Box>
			{/* edit Store Data */}
			<EditModal
				columns={inputColumns}
				allStores={allStores}
				setAllStores={setAllStores}
				selectedStore={selectedStore}
				setSelectedStore={setSelectedStore}
				modalOpen={modalOpen}
				setModalOpen={setModalOpen}
				submitFunction={submitFunction}
			/>
			<FetchNewStoresDialog open={fetchDialogOpen} onClose={() => setFetchDialogOpen(false)} />
			<StoreMasterUploadDialog
				open={uploadDialogOpen}
				onClose={() => setUploadDialogOpen(false)}
				onUploadSuccess={fetchStores}
			/>
		</Box>
	);
};

export default StoreTidMaster;
